import { useEffect, useRef, useState } from "react";
import { InfiniteCanvas } from "../../../widgets/infinitecanvas";
import styles from "./workspace.module.css";
import { useRoomStore } from "../../../stores/room";
import { useUserStore } from "../../../stores/user";
import { UserStream } from "../../../widgets/user";
import { Tabs, Form, Input, Button, Select, message as ShowMessage, Spin } from "antd";
import {
  AudioOutlined,
  CameraOutlined,
  SettingOutlined,
  ArrowLeftOutlined,
  ShareAltOutlined,
  LoadingOutlined,
  DownloadOutlined
} from "@ant-design/icons";
import { ReactComponent as ArrowIcon } from '../icons/arrow-left.svg';
import { upload, isOnline } from '../../../libs/utils';
import VoxeetSDK from "@voxeet/voxeet-web-sdk";
import { MessageList } from "./messages";
import { useNavigate } from "react-router";

const WORKSTATION_BGS = [
  'man1',
  'man2',
  'man3',
  'man4',
  'woman1',
  'woman2',
  'woman3',
  'woman4',
  'woman5',
];

export function WorkspaceRoom({ onReady, onOpenSettings, onOpenShare }) {
  const navigate = useNavigate();
  const { id, participants, positions, update, members = {}, messages, files, spaceId, postFile } = useRoomStore();
  const { id: userId, name: fullName } = useUserStore();
  const [changePosition, setChangePosition] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [status, setStatus] = useState(members && members[userId]?.status || "");
  const [uploadingFile, setUploadingFile] = useState(false);
  const activeParticipants = participants.filter(
    (p) =>
      p.status === "Connected" ||
      p.status === "Connecting" ||
      p.status === "Inactive"
  );
  const me = participants.find((p) => p.pesuId === userId);
  const canvasRef = useRef(null);
  
  const [hasVideo, setHasVideo] = useState(false);

  useEffect(() => {
    if (members && members[userId]?.pos) {
      onReady && onReady({
        scale: 0.8
      });
      canvasRef.current.moveTo(members[userId]?.pos[0], members[userId]?.pos[1]);
    } else {
      setChangePosition(true);
    }
  }, []);

  useEffect(() => {
    if(me?.stream){
      setHasVideo(true);
    }
    else{
      setHasVideo(false);
    }
  }, [me?.stream]);



  return (
    <div className={styles.room}>
      <InfiniteCanvas ref={canvasRef} className={`center`}>
        {changePosition && (
          <Workstation
            name={fullName}
            canMove={true}
            shouldMove={true}
            onPosChange={(e) => {
              update(id, {
                members: {
                  [userId]: {
                    name: fullName,
                    pos: e,
                    addedOn: new Date(),
                  },
                },
              });
              setChangePosition(false);
              onReady && onReady({
                scale: 0.8
              });
            }}
            enabled={true}
            isMe={true}
          />
        )}
        {Object.keys(members || {}).map((mid) => {
          const p = participants.find((p) => p.pesuId === mid);
          return <Workstation
            canMove={mid === userId}
            name={members[mid]?.name}
            online={p && isOnline(p)}
            participant={p}
            position={members[mid]?.pos}
            status={members[mid]?.status}
            wStyle={members[mid]?.style}
            key={mid}
            enabled={mid === userId}
            isMe={mid === userId}
            onPosChange={(e) => {
              update(id, {
                members: {
                  [userId]: {
                    name: fullName,
                    pos: e,
                    addedOn: new Date(),
                  },
                },
              });
            }}
          />;
        })}
      </InfiniteCanvas>
      <div className={styles.sidebar}>
        <div className={styles.actions}>
          <Button type="ghost" icon={<ArrowLeftOutlined />} onClick={() => {
            navigate(`/spaces/${spaceId}`)
          }} />
          <div className={styles.seperator} />
          <Button type="ghost" className={me?.audioTransmitting ? '' : styles.disabled} icon={<AudioOutlined />} onClick={() => {
            if(me?.audioTransmitting){
              VoxeetSDK.conference.stopAudio(VoxeetSDK.session.participant);
            }
            else{
              VoxeetSDK.conference.startAudio(VoxeetSDK.session.participant);
            }
          }} />
          <Button type="ghost" className={hasVideo ? '' : styles.disabled} icon={<CameraOutlined />} onClick={() => {
              if(me.stream && me.stream.getVideoTracks().length > 0){
                VoxeetSDK.conference.stopVideo(VoxeetSDK.session.participant);
              }
              else{
                VoxeetSDK.conference.startVideo(VoxeetSDK.session.participant);
              }
          }} />
          <div className={styles.seperator} />
          <Button type="ghost" onClick={() => {
              canvasRef.current.moveTo(members[userId]?.pos[0], members[userId]?.pos[1]);
          }}>My Workstation</Button>
          <div className={styles.seperator} />
          <Button type="ghost" icon={<ShareAltOutlined />} onClick={onOpenShare} />
          <Button type="ghost" icon={<SettingOutlined />} onClick={onOpenSettings} />
        </div>
        <div className={styles.status}>
          <Form
            onSubmitCapture={(e) => {
              e.preventDefault();
              update(id, {
                members: {
                  [userId]: {
                    status,
                  },
                },
              });
            }}
          >
            <Form.Item>
              <Input
                placeholder="Type status message here..."
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                }}
              />
            </Form.Item>
            <Form.Item>
              <Button htmlType="submit" type="primary">Set Status</Button>
            </Form.Item>
          </Form>
        </div>
        <Tabs>
          <Tabs.TabPane tab="Members" key="members">
            <div style={{ marginBottom: "1.6rem" }}>
              <Input.Search
                placeholder="Search member"
                value={memberSearch}
                onChange={e => {
                    setMemberSearch(e.target.value);
                }}
                enterButton
              />
            </div>
            <div className={styles.memberlist}>
              {Object.keys(members || {}).map(mid => ({id: mid, ...members[mid]})).filter(m => memberSearch ? m.name.toLowerCase().indexOf(memberSearch.toLowerCase()) >= 0 : true).map((member) => {
                const online = activeParticipants.find((p) => p.pesuId === member.id);
                return (
                  <div
                    className={`${styles.memberitem} ${
                      online ? styles.online : ""
                    }`}
                    onClick={() => {
                      canvasRef.current.moveTo(member.pos[0], member.pos[1]);
                    }}
                  >
                    {member.name}
                  </div>
                );
              })}
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Chat" key="chat">
            <MessageList messages={messages} />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Files" key="files">
            <Button type="ghost" onClick={() => {
              document.getElementById('workflowfileupload').click();
            }}>Upload File</Button>
            <input type="file" id="workflowfileupload" onChange={async e => {
              const selectedFile = e.target.files[0];
              if(selectedFile.name.endsWith('.pdf') || selectedFile.name.endsWith('.zip') || selectedFile.name.endsWith('.rar')){
                ShowMessage.error('pdf, zip and rar files are not supported');
                return;
              }
              setUploadingFile(true);
              const file = await upload(selectedFile, `rooms/${id}`);
              await postFile({
                id: file.asset_id,
                url: file.secure_url,
                size: file.bytes,
                name: file.original_filename,
                created: {
                  at: new Date(),
                  by: userId
                },
                format: file.format,
                type: file.resource_type,
              });
              setUploadingFile(false);
            }} />
            {uploadingFile && <Spin spinning={true} indicator={<LoadingOutlined style={{ fontSize: 24, marginLeft: '12px' }} spin />} />}
            <div className={styles.files}>
              {[...(files || [])].reverse().map(file => {
                return <div className={styles.file} onClick={() => {
                  window.open(file.url);
                }}>
                  <div>
                    <div>{file.name}</div>
                    <div>{Math.round(file.size / 1024)}kb</div>
                  </div>
                  <div>
                    <div>
                      <DownloadOutlined />
                    </div>
                  </div>
                </div>
              })}
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Settings" key="settings">
            <div className={styles.settings}>
              <span>Workstation Style</span>
              <Select style={{ width: '100%' }} onChange={e => {
                update(id, {
                  members: {
                    [userId]: {
                      style: e,
                    },
                  },
                });
              }}>
                {WORKSTATION_BGS.map(w => {
                  return <Select.Option value={w}>
                    <div style={{ height: '200px', backgroundSize: 'cover' }} className={styles[w]} />
                  </Select.Option>
                })}
              </Select>
            </div>
          </Tabs.TabPane>
        </Tabs>
      </div>
    </div>
  );
}

export function Workstation({
  canMove,
  shouldMove,
  position = [],
  onPosChange,
  name,
  status,
  participant,
  wStyle,
  enabled,
  online,
  isMe
}) {
  const workspace = useRef(null);
  const dragStart = useRef(null);
  const workspaceStart = useRef(null);
  const videoRef = useRef(null);
  const [hasVideo, setHasVideo] = useState(false);
  const tracksCount = participant?.stream?.getTracks().length;
  const [isSpeaking, setIsSpeaking] = useState(false);
  useEffect(() => {
    console.log('WORKSTATION UPDATE', participant?.stream);
      if(participant?.stream) {
        navigator.attachMediaStream(videoRef.current, participant?.stream);
        setHasVideo(participant?.stream?.getVideoTracks().length > 0);
      }
      else{
        setHasVideo(false);
      }
  },[participant?.stream, participant?.updatedOn, tracksCount])
  useEffect(() => {
    if(participant){
      const timer = setInterval(() => {
        VoxeetSDK.conference.isSpeaking(participant.original, isSpeaking => {
            setIsSpeaking(isSpeaking)
        });
      }, 500);
      return () => {
          clearInterval(timer);
      }
    }
  }, [participant]);
  useEffect(() => {
    const onMouseMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (dragStart.current) {
        workspace.current.style.top = `${
          workspaceStart.current.top + e.clientY - dragStart.current.y
        }px`;
        workspace.current.style.left = `${
          workspaceStart.current.left + e.clientX - dragStart.current.x
        }px`;
      }
    };
    window.addEventListener('mousemove', onMouseMove);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
    }
  }, []);
  return (
    <div
      className={`${styles.workspace} ${hasVideo ? styles.hasVideo : ''} ${enabled ? styles.enabled : ''}`}
      style={{
          left: `${position[0] || 0}px`,
          top: `${position[1] || 0}px`,
          zIndex: isMe ? 100 : 0,
      }}
      ref={workspace}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (canMove) {
          dragStart.current = {
            x: e.clientX,
            y: e.clientY,
          };
          workspaceStart.current = {
            left: workspace.current.offsetLeft,
            top: workspace.current.offsetTop,
          };
        }
      }}
      onMouseUp={(e) => {
        e.preventDefault();
        e.stopPropagation();
        dragStart.current = null;
        onPosChange([
          workspace.current.offsetLeft,
          workspace.current.offsetTop,
        ]);
      }}
    >
      <div className={`${styles.nameboard} ${online ? styles.online : ""}`}>
        <div className={styles.roller}>
          <span>
            {name} - {status}
          </span>
        </div>
      </div>
      <div className={`${styles.audioindicator} ${isSpeaking ? styles.speaking : ''}`} />
      <div className={`${styles.workspaceimage} ${styles[wStyle]}`} />
      {shouldMove && <div className={styles.placementmessage}>Drag and drop your workstation in the room to start</div>}
      <div className={styles.video}>
        <div className={styles.videocontainer}>
          <video
            loop
            muted
            autoPlay
            // src="https://static-files-5ci.pages.dev/examples/videos/woman-talking-1.mp4"
            ref={videoRef}
            onError={e => {
              console.log('ERROR', e);
            }}
            onAbort={e => {
              console.log('ABORT', e);
            }}
            onChange={e => {
              console.log('CHANGE', e);
            }}
          />
        </div>
      </div>
    </div>
  );
}
